import {
  ROUTE_QUERY_SEPARATOR,
  CONSTANT_FILTERS_START_CHAR,
  ROUTER_OPERATOR_SEPARATOR,
  FILTER_ARRAY_VALUES_SEPARATOR
} from "@/data/table";
import type { FilterDef, ActiveFilter } from "./types";

const SEP = ROUTE_QUERY_SEPARATOR;
const OP_SEP = ROUTER_OPERATOR_SEPARATOR;

export interface ListingConfig {
  page: number;
  limit: number;
  /** Sort string, e.g. `-created_at`. Empty string serialises as `none`. */
  sort: string;
  filters: ActiveFilter[];
  lockedFilters?: ActiveFilter[];
}

export interface SerialiseDefaults {
  defaultLimit: number;
  /** Sort string, e.g. `-created_at`. Empty/undefined serialises as `none`. */
  defaultSort: string;
}

export interface ParsedFilterEntry {
  /** Set when the entry was prefixed with the const-filter marker. */
  locked: boolean;
  /** FilterDef.id resolved from `filterDefs` if a match was found. */
  defId?: string;
  field: string;
  operator: string;
  /** Array if the matching def is multi/tag/category, otherwise a string. */
  value: string | string[];
}

export interface DeserialisedConfig {
  page: number;
  limit: number;
  sort: string;
  entries: ParsedFilterEntry[];
}

export function serialiseValue(value: any): string {
  if (Array.isArray(value)) return value.join(FILTER_ARRAY_VALUES_SEPARATOR);
  return String(value ?? "");
}

export function serialiseFilterChunk(
  field: string,
  operator: string,
  value: string,
  locked = false
): string {
  const prefix = locked ? CONSTANT_FILTERS_START_CHAR : "";
  return `${prefix}${field}${OP_SEP}${operator}${OP_SEP}${value}`;
}

export function serialiseConfig(config: ListingConfig): string {
  const parts = [
    String(config.page),
    String(config.limit),
    config.sort || "none"
  ];

  for (const af of config.filters) {
    parts.push(
      serialiseFilterChunk(af.field, af.operator, serialiseValue(af.value))
    );
  }

  for (const af of config.lockedFilters ?? []) {
    parts.push(
      serialiseFilterChunk(
        af.field,
        af.operator,
        serialiseValue(af.value),
        true
      )
    );
  }

  return parts.join(SEP);
}

export function defaultConfigString(defaults: SerialiseDefaults): string {
  return [
    "1",
    String(defaults.defaultLimit),
    defaults.defaultSort || "none"
  ].join(SEP);
}

/**
 * Parse a config string into structured data. Returns `null` for malformed
 * input (fewer than 3 parts). Filter values are parsed as arrays when the
 * matching `FilterDef` is multi/tag/category, or when the raw value contains
 * the array separator.
 */
export function deserialiseConfig(
  raw: string,
  filterDefs?: FilterDef[]
): DeserialisedConfig | null {
  if (!raw) return null;
  const parts = raw.split(SEP);
  if (parts.length < 3) return null;

  const p = parseInt(parts[0], 10);
  const l = parseInt(parts[1], 10);

  const result: DeserialisedConfig = {
    page: !isNaN(p) && p > 0 ? p : 1,
    limit: !isNaN(l) && l > 0 ? l : 10,
    sort: parts[2] === "none" ? "" : parts[2],
    entries: []
  };

  for (let i = 3; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;

    const isConst = part[0] === CONSTANT_FILTERS_START_CHAR;
    const cleaned = isConst ? part.substring(1) : part;

    const sepIdx = cleaned.indexOf(OP_SEP);
    if (sepIdx === -1) continue;

    const field = cleaned.substring(0, sepIdx);
    const rest = cleaned.substring(sepIdx + 1);
    const sep2Idx = rest.indexOf(OP_SEP);
    if (sep2Idx === -1) continue;

    const operator = rest.substring(0, sep2Idx);
    const rawValue = rest.substring(sep2Idx + 1);

    const def = filterDefs?.find(d => d.field === field);
    const isArrayType =
      def?.multi ||
      def?.type === "tag" ||
      def?.type === "category" ||
      rawValue.includes(FILTER_ARRAY_VALUES_SEPARATOR);

    const value = isArrayType
      ? rawValue.split(FILTER_ARRAY_VALUES_SEPARATOR)
      : rawValue;

    result.entries.push({
      locked: isConst,
      defId: def?.id,
      field,
      operator,
      value
    });
  }

  return result;
}

/**
 * Split a raw config string into its top-level positional segments and
 * filter entries. Useful for stateless URL manipulation (e.g. pill toggles)
 * where full deserialisation is unnecessary.
 */
export function splitConfigString(raw: string): {
  page: string;
  limit: string;
  sort: string;
  filterEntries: string[];
} {
  const parts = raw ? raw.split(SEP) : [];
  return {
    page: parts[0] || "",
    limit: parts[1] || "",
    sort: parts[2] || "",
    filterEntries: parts.slice(3).filter(Boolean)
  };
}

/** Re-export the raw separators for callers that need them directly. */
export const SEPARATORS = {
  config: SEP,
  operator: OP_SEP,
  arrayValue: FILTER_ARRAY_VALUES_SEPARATOR,
  constPrefix: CONSTANT_FILTERS_START_CHAR
} as const;
